import { AnimatePresence, motion } from 'framer-motion';
import { Bell } from 'lucide-react';
import { InitialsAvatar, SNAPPY_SPRING } from '../portal';

interface PublisherCardProps {
  name: string;
  role: string | null;
  avatarUrl: string | null;
  unreadCount: number;
  isMenuOpen: boolean;
  onToggleMenu: () => void;
  onAlertsClick: () => void;
}

/** Figma "Component 1029": bottom-pinned publisher card with the alerts bell. */
export function PublisherCard({
  name,
  role,
  avatarUrl,
  unreadCount,
  isMenuOpen,
  onToggleMenu,
  onAlertsClick,
}: PublisherCardProps) {
  return (
    <div className="flex h-16 w-full items-center gap-2 rounded-lg bg-seeker-900 py-5 pl-4 pr-3">
      <button
        type="button"
        aria-expanded={isMenuOpen}
        onClick={onToggleMenu}
        className="flex min-w-0 flex-1 items-center gap-3 rounded-md text-left transition-opacity duration-150 hover:opacity-80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/70 focus-visible:ring-offset-2 focus-visible:ring-offset-seeker-900"
      >
        <InitialsAvatar name={name} imageUrl={avatarUrl} size={32} />
        <span className="flex min-w-0 flex-1 flex-col">
          <span
            title={name}
            className="truncate text-sm font-bold leading-5 text-white"
          >
            {name}
          </span>
          {role && (
            <span className="truncate text-xs leading-4 text-seeker-400">
              {role}
            </span>
          )}
        </span>
      </button>
      <button
        type="button"
        onClick={onAlertsClick}
        aria-label={
          unreadCount > 0 ? `Alerts, ${unreadCount} unread` : 'Alerts'
        }
        className="relative flex size-8 shrink-0 items-center justify-center rounded-full bg-seeker-800 text-white transition-colors duration-150 hover:bg-seeker-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/70 focus-visible:ring-offset-2 focus-visible:ring-offset-seeker-900"
      >
        <Bell className="size-4" />
        {/* Keyed on the count so every change re-pops; no pop on first paint. */}
        <AnimatePresence initial={false}>
          {unreadCount > 0 && (
            <motion.span
              key={unreadCount}
              aria-hidden="true"
              initial={{ scale: 0.5, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.5, opacity: 0, transition: { duration: 0.1 } }}
              transition={SNAPPY_SPRING}
              className="absolute -right-1 -top-1 flex h-4 min-w-4 items-center justify-center rounded-full bg-rose-500 px-1 text-[10px] font-bold leading-none tabular-nums text-white"
            >
              {unreadCount > 99 ? '99+' : unreadCount}
            </motion.span>
          )}
        </AnimatePresence>
      </button>
    </div>
  );
}
